// frontend/src/pages/Home.js
import React, { useEffect, useState, useRef } from "react";
import { getCourses, getSuccessStories, getWhyChooseItems, getGalleryFolders, getHeroImages } from "../services/firestoreService";
import { Link } from "react-router-dom";
import FloatingButton from "../Components/FloatingButton";

// Placeholder for default card images
const imagePlaceholderUrl = "/brainoven-logo.jpg";

// Default Main Hero Image (Static Fallback / First Carousel Item)
const defaultMainHero = {
  id: "main-default",
  imageUrl: "/internships-image.jpg",
  title: "Fueling Tech Careers",
  subtitle: "Hands-on, Industry-Focused Training and Mentorship.",
  buttonText: "Explore Our Programs",
  link: "/services",
};

// --- COMMON SCROLL STYLES ---
const scrollContainerStyle = {
  display: "flex",
  overflowX: "scroll",
  scrollSnapType: "x mandatory",
  paddingBottom: "1rem",
  scrollBehavior: "smooth",
};

const contentScrollItemStyle = {
  flex: "0 0 320px",
  scrollSnapAlign: "start",
  marginRight: "1rem",
};

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [stories, setStories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [whyChooseItems, setWhyChooseItems] = useState([]);
  const [loadingWhy, setLoadingWhy] = useState(true);
  const [additionalHeroImages, setAdditionalHeroImages] = useState([]);

  // Carousel State
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [isHeroHovered, setIsHeroHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Refs for scrollable sections
  const whyRef = useRef(null);
  const storiesRef = useRef(null);

  // Combine Main Hero Image as #1 with additional Firestore images
  const combinedHeroImages = [
    defaultMainHero,
    ...additionalHeroImages.map((item, idx) => ({
      id: item.id || item._id || `additional-${idx}`,
      imageUrl: item.imageUrl,
      title: item.title || "Fueling Tech Careers",
      subtitle: item.subtitle || "Hands-on, Industry-Focused Training and Mentorship.",
      buttonText: item.buttonText || "Explore Our Programs",
      link: item.link || "/services",
    }))
  ];

  useEffect(() => {
    getCourses().then(setCourses).catch(() => {});
    getSuccessStories().then(setStories).catch(() => {});
    getGalleryFolders()
      .then((folders) => {
        const allImages = folders.flatMap((f) => f.images || []);
        setGallery(allImages);
      })
      .catch(() => {});
    getWhyChooseItems()
      .then((items) => {
        setWhyChooseItems(items || []);
      })
      .catch((err) => {
        console.error("Error fetching Why Choose items:", err);
      })
      .finally(() => {
        setLoadingWhy(false);
      });

    // Fetch Additional Hero Images from Firestore
    getHeroImages()
      .then((items) => {
        setAdditionalHeroImages(items || []);
      })
      .catch((err) => {
        console.error("Error fetching hero images:", err);
      });
  }, []);

  // Continuous Auto-scroll Effect (auto-advances every 4 seconds when not hovered)
  useEffect(() => {
    if (combinedHeroImages.length <= 1 || isHeroHovered) {
      return;
    }

    const timer = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % combinedHeroImages.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [combinedHeroImages.length, isHeroHovered, currentHeroIndex]);

  const handlePrevHero = () => {
    setCurrentHeroIndex((prev) => (prev - 1 + combinedHeroImages.length) % combinedHeroImages.length);
  };

  const handleNextHero = () => {
    setCurrentHeroIndex((prev) => (prev + 1) % combinedHeroImages.length);
  };

  // Touch Swipe Handlers for Mobile Devices
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 45;
    if (distance > minSwipeDistance) {
      // Swiped Left -> Next
      handleNextHero();
    } else if (distance < -minSwipeDistance) {
      // Swiped Right -> Prev
      handlePrevHero();
    }
  };

  // Scroll helpers for other sections
  const scrollBy = (ref, amount) => {
    if (!ref.current) return;
    ref.current.scrollBy({ left: amount, behavior: "smooth" });
  };

  const contentCardWidth = 320 + 16;
  const scrollContentRight = (ref) => scrollBy(ref, contentCardWidth);
  const scrollContentLeft = (ref) => scrollBy(ref, -contentCardWidth);

  return (
    <div className="bg-light pb-5">
      <style>{`
        /* Hide default scrollbar for modern browsers */
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        /* Arrow controls for other sections */
        .scroll-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 70;
          width: 44px;
          height: 44px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(33,37,41,0.72);
          border-radius: 999px;
          color: white;
          border: none;
          cursor: pointer;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.35);
        }
        .scroll-arrow:hover { transform: translateY(-50%) scale(1.03); background: rgba(33,37,41,0.9); }

        .scroll-arrow.left { left: 16px; }
        .scroll-arrow.right { right: 16px; }

        @media (max-width: 576px) {
          .scroll-arrow { width: 38px; height: 38px; background: rgba(0,0,0,0.55); }
          .scroll-arrow.left { left: 8px; }
          .scroll-arrow.right { right: 8px; }
        }

        .scroll-wrapper { position: relative; }

        /* HERO CAROUSEL & ARROWS */
        .hero-carousel-container {
          position: relative;
          user-select: none;
        }

        .hero-nav-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 25;
          width: 46px;
          height: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(15, 23, 42, 0.7);
          backdrop-filter: blur(8px);
          border: 1.5px solid rgba(255, 255, 255, 0.35);
          border-radius: 50%;
          color: #ffffff;
          font-size: 20px;
          line-height: 1;
          cursor: pointer;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
          transition: all 0.2s ease;
        }

        .hero-nav-arrow:hover {
          background: rgba(15, 23, 42, 0.92);
          border-color: rgba(255, 255, 255, 0.75);
          transform: translateY(-50%) scale(1.08);
        }

        .hero-nav-arrow:active {
          transform: translateY(-50%) scale(0.96);
        }

        .hero-nav-arrow-left {
          left: 20px;
        }

        .hero-nav-arrow-right {
          right: 20px;
        }

        @media (max-width: 576px) {
          .hero-nav-arrow {
            width: 38px;
            height: 38px;
            font-size: 16px;
          }
          .hero-nav-arrow-left {
            left: 10px;
          }
          .hero-nav-arrow-right {
            right: 10px;
          }
        }

        .hero-indicators {
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 8px;
          z-index: 25;
        }

        .hero-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.6);
          background: rgba(255, 255, 255, 0.35);
          padding: 0;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .hero-dot.active {
          width: 24px;
          border-radius: 999px;
          background: #ffffff;
          border-color: #ffffff;
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
        }

        .btn-hero {
          background: linear-gradient(90deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06));
          color: #fff;
          border: 1px solid rgba(255,255,255,0.3);
          padding: 12px 24px;
          border-radius: 999px;
          font-weight: 700;
          text-transform: none;
          box-shadow: 0 8px 30px rgba(6, 12, 34, 0.35);
          transition: transform .18s ease, background .18s ease, box-shadow .18s ease;
          backdrop-filter: blur(4px);
          text-decoration: none;
        }
        .btn-hero:hover {
          transform: translateY(-3px);
          background: linear-gradient(90deg, rgba(255,255,255,0.22), rgba(255,255,255,0.12));
          box-shadow: 0 12px 36px rgba(6,12,34,0.45);
          color: #fff;
        }

        header.mb-5.shadow-lg {
          box-shadow: 0 14px 40px rgba(6,12,34,0.14);
        }

        .hero-watermark {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: clamp(180px, 30vw, 360px);
          height: clamp(180px, 30vw, 360px);
          background-image: url("/brainoven-logo.jpg");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          opacity: 0.18;
          pointer-events: none;
          z-index: 1;
        }
      `}</style>

      {/* 1. HEADER: Auto-scrollable Hero Carousel with Watermark */}
      <header 
        className="mb-5 shadow-lg overflow-hidden hero-carousel-container" 
        style={{ position: "relative" }}
        onMouseEnter={() => setIsHeroHovered(true)}
        onMouseLeave={() => setIsHeroHovered(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="hero-slider-track"
          style={{
            display: "flex",
            transform: `translateX(-${currentHeroIndex * (100 / combinedHeroImages.length)}%)`,
            transition: "transform 0.65s cubic-bezier(0.25, 1, 0.5, 1)",
            width: `${combinedHeroImages.length * 100}%`
          }}
        >
          {combinedHeroImages.map((hero) => (
            <div 
              key={hero.id} 
              style={{ 
                flex: `0 0 ${100 / combinedHeroImages.length}%`, 
                width: `${100 / combinedHeroImages.length}%` 
              }}
            >
              <div
                className="text-white p-5 text-center"
                style={{
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${hero.imageUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  minHeight: "420px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  position: "relative"
                }}
              >
                {/* Background Watermark Image */}
                <div className="hero-watermark" />

                <div className="container" style={{ position: "relative", zIndex: 2 }}>
                  <h1 className="display-3 fw-bolder mb-3">{hero.title}</h1>
                  <p className="lead fs-4" style={{ color: "#e9ecef" }}>
                    {hero.subtitle}
                  </p>

                  <Link to={hero.link} className="btn-hero mt-3 d-inline-block">
                    {hero.buttonText}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Manual Navigation Arrows (Rendered when multiple hero images exist) */}
        {combinedHeroImages.length > 1 && (
          <>
            <button
              className="hero-nav-arrow hero-nav-arrow-left"
              onClick={handlePrevHero}
              aria-label="Previous image"
              title="Previous image"
              type="button"
            >
              &#10094;
            </button>
            <button
              className="hero-nav-arrow hero-nav-arrow-right"
              onClick={handleNextHero}
              aria-label="Next image"
              title="Next image"
              type="button"
            >
              &#10095;
            </button>

            {/* Indicator dots */}
            <div className="hero-indicators">
              {combinedHeroImages.map((_, dotIdx) => (
                <button
                  key={dotIdx}
                  className={`hero-dot ${dotIdx === currentHeroIndex ? "active" : ""}`}
                  onClick={() => setCurrentHeroIndex(dotIdx)}
                  aria-label={`Go to slide ${dotIdx + 1}`}
                  type="button"
                />
              ))}
            </div>
          </>
        )}
      </header>

      {/* All subsequent sections */}
      <div className="container">
        {/* 2. ABOUT US SUMMARY */}
        <section className="mb-5">
          <h2 className="text-center mb-4 text-dark display-6 fw-bold">
            About BrainOven
          </h2>
          <div className="row g-4 justify-content-center">
            <div className="col-lg-4 col-md-6">
              <div className="card h-100 shadow-sm p-4 border-0">
                <h3 className="card-title fs-5 fw-bold text-dark">
                  Why the name BrainOven?
                </h3>
                <p className="card-text text-muted">
                  At <strong>BrainOven</strong> we <strong>'bake' raw talent</strong>{" "}
                  into industry-ready tech professionals — thoughtfully guided,
                  applied, and tested.
                </p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="card h-100 shadow-sm p-4 border-0">
                <h3 className="card-title fs-5 fw-bold text-dark">Mission</h3>
                <p className="card-text text-muted">
                  "Transforming raw talent into skilled tech professionals" by
                  focusing on practical, industry-relevant skills.
                </p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="card h-100 shadow-sm p-4 border-0">
                <h3 className="card-title fs-5 fw-bold text-dark">
                  Trainers' Experience
                </h3>
                <p className="card-text text-muted">
                  Our trainers bring years of <strong>real industry
                  experience</strong>, mentoring students with practical projects
                  and live problem solving.
                </p>
              </div>
            </div>
          </div>
        </section>

        <hr className="my-5" />

        {/* 3. WHY BRAINOVEN */}
        <section className="mb-5">
          <h2 className="text-center mb-4 text-dark display-6 fw-bold">
            Why Choose BrainOven?
          </h2>

          <div className="scroll-wrapper mb-3">
            <button
              aria-label="Scroll why left"
              className="scroll-arrow left"
              onClick={() => scrollContentLeft(whyRef)}
              title="Previous"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M15.41 7.41L10.83 12l4.58 4.59L14 18l-6-6 6-6 1.41 1.41z" fill="currentColor"/>
              </svg>
            </button>

            <button
              aria-label="Scroll why right"
              className="scroll-arrow right"
              onClick={() => scrollContentRight(whyRef)}
              title="Next"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"/>
              </svg>
            </button>

            <div
              ref={whyRef}
              className="d-flex hide-scrollbar"
              style={scrollContainerStyle}
            >
              {loadingWhy && (
                <div className="col">
                  <p className="text-muted">Loading why choose items...</p>
                </div>
              )}
              {!loadingWhy && whyChooseItems.length === 0 && (
                <div className="col">
                  <p className="text-muted">No why choose items available.</p>
                </div>
              )}
              {!loadingWhy && whyChooseItems.map((item) => (
                <div key={item._id || item.id} style={contentScrollItemStyle}>
                  <div
                    className="card text-white bg-dark border-secondary h-100 shadow-lg"
                    style={{
                      position: "relative",
                      borderRadius: "12px",
                      overflow: "hidden",
                      minHeight: "260px",
                    }}
                  >
                    <img
                      src={item.imageUrl || imagePlaceholderUrl}
                      alt={item.title}
                      className="card-img"
                      loading="lazy"
                      decoding="async"
                      style={{
                        height: "100%",
                        objectFit: "cover",
                        filter: "brightness(0.55)",
                      }}
                      onError={(e) => {
                        e.target.src = imagePlaceholderUrl;
                      }}
                    />

                    <div
                      className="card-img-overlay d-flex flex-column justify-content-end p-4"
                      style={{
                        background:
                          "linear-gradient(180deg, transparent 20%, rgba(0,0,0,0.85))",
                      }}
                    >
                      <h3 className="card-title fs-5 fw-bold mb-1">
                        {item.title}
                      </h3>
                      <p
                        className="card-text small mb-0"
                        style={{ color: "#dcdcdc" }}
                      >
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <hr className="my-5" />

        {/* 4. SUCCESS STORIES */}
        <section className="mb-5">
          <h2 className="text-center mb-4 text-dark display-6 fw-bold">
            Student Success Stories 🎓
          </h2>

          <div className="scroll-wrapper mb-3">
            <button
              aria-label="Scroll stories left"
              className="scroll-arrow left"
              onClick={() => scrollContentLeft(storiesRef)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M15.41 7.41L10.83 12l4.58 4.59L14 18l-6-6 6-6 1.41 1.41z" fill="currentColor"/>
              </svg>
            </button>

            <button
              aria-label="Scroll stories right"
              className="scroll-arrow right"
              onClick={() => scrollContentRight(storiesRef)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"/>
              </svg>
            </button>

            <div
              ref={storiesRef}
              className="d-flex hide-scrollbar"
              style={scrollContainerStyle}
            >
              {stories.length === 0 && (
                <div className="col">
                  <p className="text-muted">No success stories yet.</p>
                </div>
              )}
              {stories.map((s) => (
                <div key={s._id} style={contentScrollItemStyle}>
                  <div className="card h-100 bg-white border-secondary shadow-sm">
                    <img
                      src={s.imageUrl || "/brainoven-logo.jpg"}
                      alt={s.title}
                      className="card-img-top"
                      loading="lazy"
                      decoding="async"
                      style={{ height: "350px", objectFit: "cover", overflow: "hidden" }}
                      onError={(e) => {
                        e.target.src = "/brainoven-logo.jpg";
                      }}
                    />
                    <div className="card-body">
                      <h4 className="card-title fs-5 fw-bold">
                        {s.studentName}
                      </h4>
                      <p className="card-subtitle mb-2 text-primary small">
                        {s.title}
                      </p>
                      <p className="card-text small mt-3 text-muted">
                        {s.content?.slice(0, 120)}
                        {s.content?.length > 120 ? "..." : ""}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <FloatingButton />
    </div>
  );
}